import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserPlan } from '../types/billing';

const API_BASE_URL =
  process.env.REACT_APP_SERVER_URL ||
  (typeof window !== 'undefined' &&
  window.location.hostname !== 'localhost' &&
  window.location.hostname !== '127.0.0.1'
    ? ''
    : 'http://localhost:3001');

interface UseTrialReturn {
  userPlan: UserPlan | null;
  isLoading: boolean;
  error: string | null;
}

export function useTrial(): UseTrialReturn {
  const { user, getAuthToken } = useAuth();
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBilling = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUserPlan(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/billing`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch billing info');
      }

      const data = await res.json();
      setUserPlan(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling, user]);

  return { userPlan, isLoading, error };
}
